/**
 * RESPONSIBILITY: the bulletins, newest first, as this browser last fetched them.
 *
 * Held for FORTY MINUTES while the site is open (James, 2026-10-17), and the PDF itself
 * is kept by the browser for the same forty minutes (see bulletin-routes.ts). Keyed to
 * the FOLDER, so pointing the Bulletin section at a different folder is seen at once
 * rather than forty minutes later (R21).
 */
import { useQuery } from '@tanstack/react-query';
import { APP_ROUTES } from '../../app-routes';
import { localDay } from '../commons/local-day';

export interface Bulletin {
  id: string;
  title: string;
  /** YYYY-MM-DD: the Sunday it is for, or the day it was uploaded. */
  date: string;
  /** Which upload of the file — part of its address, so a replaced one is never stale. */
  version: string;
}

const FORTY_MINUTES = 40 * 60 * 1000;

export function useBulletins(folder: string | undefined): Bulletin[] {
  const { data } = useQuery({
    queryKey: ['bulletins', folder ?? ''],
    queryFn: async (): Promise<Bulletin[]> => {
      const res = await fetch(APP_ROUTES.bulletins);
      if (!res.ok) throw new Error(`bulletin list request failed: ${res.status}`);
      return ((await res.json()) as { bulletins: Bulletin[] }).bulletins;
    },
    staleTime: FORTY_MINUTES,
  });
  return data ?? [];
}

export const bulletinHref = (b: Bulletin): string => APP_ROUTES.bulletinPdf(b.id, b.version);

/**
 * The coming Sunday, or today when today is a Sunday (James, 2026-10-17).
 */
export function upcomingSunday(now = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + ((7 - now.getDay()) % 7));
}

/**
 * This week's: the Sunday in its file name (bulletin-MM-DD-YYYY) is the coming one —
 * today, on a Sunday. Anything else is only the latest, and is called that (R70).
 */
export function isThisWeeks(b: Bulletin, now = new Date()): boolean {
  const day = localDay(b.date);
  return day !== null && day.getTime() === upcomingSunday(now).getTime();
}
